// ── Loan Funds tab — participant funds on a loan ──
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { fmtDate } from "../../../shared/format";
import { Badge, Btn, Card, Icon, STh, Spinner, Td, TdNum, Th } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

export const LoanFundsTab = ({ loanid, canEdit, canDelete, onReload }) => {
    const { api, delRec, setConfirm, setModal, view } = useApp();
    const [loanFunds, setLoanFunds] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
      setLoading(true);
      api.get('getLoanFunds?loanid=' + loanid)
        .then(d => { setLoanFunds(d); setLoading(false); })
        .catch(() => setLoading(false));
    };
    useEffect(() => { load(); }, [loanid]);

    const active = loanFunds.filter(lf => !lf.enddate);
    const totalActivePct = active.reduce((s, lf) => s + parseFloat(lf.ownershippct || 0), 0);
    const { sorted, sortKey, sortDir, toggle } = useSortable(loanFunds, 'effectivedate', 'desc');

    if (loading) return <Spinner />;

    return <>
      {/* Ownership summary */}
      {active.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {active.map(lf => (
            <div key={lf.id} style={{ background: B.cyanLight, borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
              <span style={{ fontWeight: 600, color: B.cyanDark }}>{lf.fundname}</span>
              <span style={{ color: B.cyanDark, marginLeft: 8 }}>{parseFloat(lf.ownershippct).toFixed(2)}%</span>
              <span style={{ color: 'var(--color-text-secondary)', marginLeft: 6, fontSize: 11 }}>since {fmtDate(lf.effectivedate)}</span>
            </div>
          ))}
          {totalActivePct < 100 && (
            <div style={{ background: '#FAEEDA', borderRadius: 8, padding: '8px 14px', fontSize: 12, color: '#BA7517' }}>
              ⚠ {(100 - totalActivePct).toFixed(2)}% unallocated
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        {canEdit && (
          <Btn variant="primary" onClick={() => setModal({ type: 'loanfund-add', loanid, onDone: load })}>
            + Add Fund Ownership
          </Btn>
        )}
      </div>

      {loanFunds.length === 0
        ? <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', padding: 12 }}>No fund ownership recorded.</div>
        : <Card style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', whiteSpace: 'nowrap' }}>
              <thead><tr>
                <STh label="Fund"           sortKey="fundname"      currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Ownership %"    sortKey="ownershippct"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
                <STh label="Effective Date" sortKey="effectivedate" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="End Date"       sortKey="enddate"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Status"         sortKey="enddate"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <Th></Th>
              </tr></thead>
              <tbody>
                {sorted.map(lf => (
                  <tr key={lf.id}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--color-background-secondary)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <Td style={{ fontWeight: 500 }}>{lf.fundname}</Td>
                    <TdNum>{parseFloat(lf.ownershippct).toFixed(2)}%</TdNum>
                    <Td>{fmtDate(lf.effectivedate)}</Td>
                    <Td>{lf.enddate ? fmtDate(lf.enddate) : '—'}</Td>
                    <Td><Badge s={lf.enddate ? 'Inactive' : 'Active'} /></Td>
                    <Td><div style={{ display: 'flex', gap: 6 }}>
                      <Btn onClick={() => setModal({ type: 'loanfund-view', rec: lf, loanid })}><Icon type="view" /></Btn>
                      {canEdit && <Btn onClick={() => setModal({ type: 'loanfund-edit', rec: lf, loanid, onDone: load })}><Icon type="edit" /></Btn>}
                      {canDelete && (
                        <Btn variant="danger" onClick={() => setConfirm({
                          msg: 'Delete this fund ownership record?',
                          onConfirm: () => delRec('LoanFunds', lf.id, 'Deleted loan fund ownership', load)
                        })}><Icon type="delete" /></Btn>
                      )}
                    </div></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
      }
    </>;
  };
